import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Directive,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {
  CoherenceEquationServiceClient,
  EntityServiceClient,
  PredictionModelDto,
  PredictionModelServiceClient,
  RawMaterialNutrientDto,
  ReferenceDto,
  ExportMultiFormatRequestDto,
  ComputeWithMetaDataDto,
  RawMaterialDto,
  SampleNutrientDto
} from '@sharedcommon/rest/data.restservice';
import {AbstractControl, FormControl} from '@angular/forms';
import {UserService} from '@sharedcommon/services/user.service';
import {Constants} from '@sharedcommon/util/constants.util';
import {NeqTableComponent} from '@sharedcommon/components/table/neq-table.component';
import {
  CalcRequestDto,
  CalcRequestInputDto,
  ComputeDto,
  EquationManagerServiceClient
} from '@sharedcommon/rest/equation-manager.restservice';
import {catchError, finalize, map, takeUntil} from 'rxjs/operators';
import {BehaviorSubject, EMPTY, Subject, Subscription, lastValueFrom } from 'rxjs';
import {ToastService} from '@sharedcommon/services/toast.service';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, Router} from '@angular/router';
import {HeaderService} from '@sharedcommon/services/header.service';
import {EntityDto, SubEntityServiceClient} from '@sharedcommon/rest/identity.restservice';
import {NeqAbstractViewComponent} from '@sharedcommon/components/validation/abstract-view.component';
import {ValidationService} from '@sharedcommon/services/validation.service';
import {NeqGaugeComponent} from '@sharedcommon/components/gauge/gauge.component';
import {Rights} from '@sharedcommon/util/rights.util';
import {FunctionUtils} from '@sharedcommon/util/functions.util';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {NeqErrorModel} from '@sharedcommon/model/neq-error.model';
import {URNUtil} from '@sharedcommon/util/urn.util';
import {NeqDialogService} from '@sharedcommon/services/dialog.service';
import {ExportService} from '@usercommon/service/export.service';
import {TechnicalDocService} from '@sharedcommon/services/technical-doc.service';
import {PrimengTableCols} from '@sharedcommon/interfaces/primeng-table-cols';
import {PrimeTableDataModel} from '@sharedcommon/model/prime-table-data.model';
import {MatDialog} from '@angular/material/dialog';
import {CalculationStatusService} from '@usercommon/service/calculation-status.service';
import {CalculationStatus} from '@sharedcommon/model/calculation-status.model';
import {FloorCeilCalculationService} from '@sharedcommon/rest/floor-ceil-calculation.service';
import {
  ProductTableSelection as ProductTableValueSelection,
  RawMaterialOrSampleNutrientValueTableData
} from '@sharedcommon/model/raw-material-or-sample-table-value-data.model';
import {ResultColumnDto} from '@sharedcommon/model/result-column.model';
import {MatIconRegistry} from '@angular/material/icon';
import {
  RawmaterialsExportDialogComponent
} from '@usercommon/components/rawmaterials/rawmaterials-export-dialog/rawmaterials-export-dialog.component';
import {ImportSemiAutoStore} from '@usercommon/stores/import-semiauto.store';
import {ImportType} from '@sharedcommon/model/import-type.model';
import {
  NeqRawMaterialBlockedResultsPopinComponent
} from '@usercommon/components/rawmaterials/rawmaterial-blocked-results-popin/rawmaterial-blocked-results-popin.component';
import {
  NeqRawmaterialsValuesDialogComponent
} from '@usercommon/components/rawmaterials/dialog/rawmaterials-values.dialog';
import {SampleDto} from '@sharedcommon/model/sample/sample.model';
import {RawmaterialOrSampleService} from '@usercommon/service/rawmaterial-or-sample.service';
import {AnalysisOriginRestService} from '@sharedcommon/rest/sample/analysis-origin.restservice';
import {
  AnalysisOriginDto,
  CALCULATED_ANALYSIS_CODE,
  STD_ANALYSIS_CODE
} from '@sharedcommon/model/sample/analysis-origin.model';
import {RawmaterialOrSampleStatus} from '@sharedcommon/util/rawmaterial-or-sample-status.utils';
import {
  SimpleReferencesSearchPopinComponent
} from "@usercommon/components/simple-references-search-popin/simple-references-search-popin.component";
import { SampleService } from '@usercommon/service/sample.service';
import { ActivityUtil } from '@sharedcommon/util/activity.util';

class FloorCeilResult {
  limitReached: boolean;
  floorVal?: number;
  ceilVal?: number;
}

/**
 * The translation interfaces which list the
 * difference needed translation which is different between the
 * different operating mode (currently FEED or SAMPLE)
 */
interface Translations {
  edit: string,
  lockedBy: string,
  disabledBy: string,
  archivedBy: string,
  editRef: string
}

const FEED_TRANSLATIONS: Translations = {
  edit: 'msg.common.icon.rawmaterial.edit',
  lockedBy: 'error.user.rawmaterial.locked_by',
  disabledBy: 'error.user.rawmaterial.disabled_by',
  archivedBy: 'error.user.rawmaterial.archived_by',
  editRef: 'msg.common.icon.rawmaterials.edit'
}

const SAMPLE_TRANSLATIONS: Translations = {
  edit: 'msg.common.icon.sample.edit',
  lockedBy: 'error.user.sample.locked_by',
  disabledBy: 'error.user.sample.locked_by',
  archivedBy: 'error.user.sample.locked_by',
  editRef: null
};

const FEED_COLUMNS: PrimengTableCols[] = [
  {field: 'label', header: 'label.common.nutrient'},
  {field: 'unit', header: 'label.common.unit'},
  {field: 'value', header: 'label.user.rawmaterial.value'},
  {field: 'proposed', header: 'label.user.rawmaterial.proposed'},
  {field: 'registered', header: 'label.user.rawmaterial.registered'},
  {field: 'diff', header: 'label.user.rawmaterial.diff'},
  {field: 'standard', header: 'label.user.rawmaterial.standard'},
  {field: 'min', header: 'label.user.rawmaterial.min'},
  {field: 'max', header: 'label.user.rawmaterial.max'}
];

const SAMPLE_COLUMNS: PrimengTableCols[] = [
  {field: 'label', header: 'label.common.nutrient'},
  {field: 'analysis', header: 'label.common.analysis'},
  {field: 'unit', header: 'label.common.unit'},
  {field: 'value', header: 'label.user.rawmaterial.value'},
  {field: 'proposed', header: 'label.user.rawmaterial.proposed'},
  {field: 'registered', header: 'label.user.rawmaterial.registered'},
  {field: 'diff', header: 'label.user.rawmaterial.diff'},
  {field: 'standard', header: 'label.user.rawmaterial.standard'},
  {field: 'min', header: 'label.user.rawmaterial.min'},
  {field: 'max', header: 'label.user.rawmaterial.max'}
];

const STD_AND_CALC_ANALYSIS_CODE = [STD_ANALYSIS_CODE, CALCULATED_ANALYSIS_CODE];

@Directive({selector: '[nutrimentValue]'})
export class NutrimentValueDirective {
  @Input() nutrimentValue: any;
  @Input() entity: RawMaterialNutrientDto;
}

@Component({
  selector: 'neq-rawmaterials-or-samples-values',
  templateUrl: './rawmaterials-or-samples-values.component.html',
  styleUrls: ['./rawmaterials-or-samples-values.component.scss']
})
export class NeqRawmaterialsOrSamplesComponent extends NeqAbstractViewComponent implements OnInit, AfterViewInit, OnDestroy, AfterContentChecked {

  /**
   * Raw material or sample ID
   */
  @Input() productId: string;
  @Output() readOnlyChange = new EventEmitter<boolean>();
  @Output() computeChange = new EventEmitter<ComputeWithMetaDataDto>();
  @Output() hasBeenTouchedChange = new EventEmitter<boolean>();
  @ViewChildren(NutrimentValueDirective) valueFields: QueryList<NutrimentValueDirective>;
  @ViewChild(NeqTableComponent) table: NeqTableComponent<any>;
  @ViewChild(NeqGaugeComponent) gauge: NeqGaugeComponent;
  @Input()
  get hasBeenTouched (): boolean {
    return this.hasBeenTouchedValue;
  };
  set hasBeenTouched(value: boolean) {
    this.hasBeenTouchedValue = value;
    this.hasBeenTouchedChange.next(value);
    this.validationService.dirty = value;
  }

  readonly ERROR_READ_ONLY = 'readonly';
  readonly ERROR_INVALID = 'invalid';
  readonly ERROR_TOLERANCE = 'tolerance';
  readonly ERROR_COHERENCE = 'coherence';

  cols: PrimengTableCols[];

  hasBeenTouchedValue: boolean = false;
  public rawMaterialOrSample: SampleDto | RawMaterialDto;
  public entity: EntityDto;
  public entityLogo: SafeUrl;
  public predictionModel: PredictionModelDto;
  public reference: ReferenceDto;
  public isReadOnly = true;
  public nutrients: { [nutrientCode: string]: RawMaterialOrSampleNutrientValueTableData } = {};
  public coherences: string[];
  public loading = true;
  public calculationId: string;
  public coherenceResult: number;
  public notes = new FormControl({
    disabled: this.isReadOnly
  });

  /**
   * List of the value for the current nutriment
   */
  currentNutriment: PrimeTableDataModel;
  public forcedCalcStatusForm: FormControl = new FormControl();

  loadingBlockedResults: boolean = false;
  waitingForBlockedResultsForPopin: boolean = false;
  allBlockedResults: BehaviorSubject<ResultColumnDto[]> = new BehaviorSubject<ResultColumnDto[]>(null);
  resetCurrentBlockedResultsTreatment: Subject<any> = new Subject();

  computeWithMeta: ComputeWithMetaDataDto;

  currentCalculationStatus: CalculationStatus;

  public canShowCheckboxes: boolean = false;

  forceRmStatusRight = [Rights.U_RM_STATUS_FORCE];

  hasRights: boolean;

  readonly PATTERN_DECIMAL = Constants.REGEX_DECIMAL;

  localAsiDialog: NeqDialogService;

  unsubscribe: Subject<any> = new Subject();

  loadingForceStatus: boolean = false;

  firstMergeOfResults: boolean = true;

  INFO_ICON_SVG = "info"

  private importSemiAutoIsOnGoing: boolean = false;
  private subscriptionForSemiautoProcessState: Subscription;
  pathComplement: string;

  /**
   * Will contain all traduction key to translate
   */
  traductionKeys: Translations;

  loadingAnalysis: boolean = false;
  /**
   * will contain all analysises allowed for none selected nutrient.
   * The selection is for applying std or proposed value.
   */
  allAnalysisesWithExclusion: AnalysisOriginDto[];
  calcAnalysis: AnalysisOriginDto = null;
  calcAnalysisArray: AnalysisOriginDto[] = [];
  stdAnalysis: AnalysisOriginDto = null;
  stdAnalysisArray: AnalysisOriginDto[] = [];
  analysisFromSample: AnalysisOriginDto = null;

  isSampleMode: boolean;
  referenceAreChanged: boolean = false;

  constructor(
    private readonly entityService: EntityServiceClient,
    private readonly rawMatOrSampleService: RawmaterialOrSampleService,
    private readonly userService: UserService,
    private readonly translate: TranslateService,
    private readonly dialogService: NeqDialogService,
    private readonly dialogMat: MatDialog,
    private readonly toaster: ToastService,
    private readonly equationManagerService: EquationManagerServiceClient,
    private readonly router: Router,
    private readonly headerService: HeaderService,
    private readonly domSanitizer: DomSanitizer,
    private readonly subEntityService: SubEntityServiceClient,
    private readonly activatedRoute: ActivatedRoute, validation: ValidationService,
    private readonly predictionModelService: PredictionModelServiceClient,
    private readonly coherenceService: CoherenceEquationServiceClient,
    private readonly exportService: ExportService,
    private readonly technicalDocService: TechnicalDocService,
    private readonly iconRegistry: MatIconRegistry, private readonly sanitizer: DomSanitizer,
    private readonly importSemiAutoStore: ImportSemiAutoStore,
    private readonly cd: ChangeDetectorRef,
    private readonly analysisService: AnalysisOriginRestService,
    private readonly sampleService: SampleService
  ) {
    super(validation);
    this.validationService.init();
    this.validationService.notes = this.notes;
    this.pathComplement = this.isFeedMode() ? 'rawmaterials' : 'samples';
    this.localAsiDialog = dialogService;
    this.updateIsReadOnly(this.isReadOnly, false);
    this.forcedCalcStatusForm.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.onForceCalcStatus();
      });
    this.isSampleMode = !this.isFeedMode();
    // Set the right translations keys
    this.traductionKeys = this.isSampleMode ? SAMPLE_TRANSLATIONS : FEED_TRANSLATIONS;
    this.cols = this.isSampleMode ? SAMPLE_COLUMNS : FEED_COLUMNS;
    if (this.isSampleMode) {
      this.loadingAnalysis = true;
      // Retrieve the analysis only if need
      this.analysisService.getAll()
        .subscribe((allAnalysis) => {
          this.allAnalysisesWithExclusion = allAnalysis
            .filter(analysis => {
              const analysisCode = analysis.code;
              let isAllowed = false;
              if (analysisCode === STD_ANALYSIS_CODE) {
                this.stdAnalysis = analysis;
                this.stdAnalysisArray = [analysis];
              } else if (analysisCode === CALCULATED_ANALYSIS_CODE) {
                this.calcAnalysis = analysis;
                this.calcAnalysisArray = [analysis];
              } else {
                isAllowed = true;
              }
              return isAllowed;
            });
          // Trig it in case the analysis arrived after all the other retrieved data
          this.updateAllSelectedAnalysis();
          this.loadingAnalysis = false;
        });
    }
  }

  ngOnInit() {
    this.validationService.dirty = this.hasBeenTouched;
    /* Do not pas by neq-icon because it is hard to override the default color of it */
    this.iconRegistry.addSvgIcon(this.INFO_ICON_SVG, this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/${this.INFO_ICON_SVG}.svg`));
    this.loading = true;
    this.calculationId = this.rawMatOrSampleService.getCalculationId();
    this.computeWithMeta = this.rawMatOrSampleService.getComputeWithMeta();
    this.currentCalculationStatus = this.rawMatOrSampleService.getCurrentCalculationStatus();

    this.reference = this.rawMatOrSampleService.getReference();
    this.predictionModel = this.rawMatOrSampleService.getPredictionModel();
    this.referenceAreChanged = this.rawMatOrSampleService.getReferenceAreChanged();

    this.allBlockedResults.next(this.rawMatOrSampleService.getAllBlockedResults() || null);
    if (this.userService.isFeedOperatingMode()) {
      this.hasRights = this.userService.hasRights([Rights.U_MP_CALCULATE]);
    }
    this.resetCurrentBlockedResultsTreatment
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        /* if we need to reset the current treatment then reset all flag */
        this.loadingBlockedResults = false;
        this.waitingForBlockedResultsForPopin = false;
      });
    this.allBlockedResults
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(allBlocked => {
        this.rawMatOrSampleService.setAllBlockedResults(allBlocked);
      });

    this.subscriptionForSemiautoProcessState = this.importSemiAutoStore.getSemiAutoProcessState()
      .subscribe((state: boolean) => {
        this.importSemiAutoIsOnGoing = state;
      });

    this.activatedRoute.params
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(params => {
      this.loading = true;
      if (this.productId && params['id'] && params['id'] !== this.productId) { // change id, reload form
        this.productId = params['id'];
        // reset data table
        this.currentNutriment.results = [];

        // force the reloading of the reference
        this.referenceAreChanged = false;
        this.entity = null;

        // reset store
        this.reset(false, false);
      }
      // init data
      this.initData();
    });

  }

  ngAfterContentChecked() {
    this.cd.detectChanges();
  }

  ngAfterViewInit() {
    this.validationService.valueFields = this.valueFields;

    // Hack to force semiauto imported nutrient as dirty.
    this.valueFields.changes
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        this.markSemiautoImportedNutrientsAsDirty();
      });
  }

  markSemiautoImportedNutrientsAsDirty() {
    if (!this.rawMatOrSampleService.isSaved() && this.importSemiAutoIsOnGoing
      && this.importSemiAutoStore.getCurrentRmValue().rawMaterial.id == this.productId) {
      for (const field of this.valueFields) {
        const n = this.importSemiAutoStore.getCurrentRmValue().inputs.find(n => n.code == field.entity.nutrientCode);
        if (n) {
          field.nutrimentValue.control.markAsDirty();
        }
      }
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
    this.rawMatOrSampleService.saveValues(this.getNutrients(), this.notes.value, this.coherenceResult);
    this.subscriptionForSemiautoProcessState?.unsubscribe();
  }

  hasFormModifications(): boolean {
    if (this.notes.value) {
      return this.valueFields
        .some((field, _idx, _items) => field.nutrimentValue.dirty) || this.notes.dirty;
    }
    return false;
  }

  save(): void {
    const saveCallback = () => {
      // Store the note and the coherence to be able to erase it in the next method call
      this.rawMatOrSampleService.storeNoteAndCoherence(this.notes.value, this.coherenceResult);
      this.rawMatOrSampleService.saveTempCalcAndUpdateAllMetaDatas(this.calculationId, this.rawMaterialOrSample)
        .subscribe(() => {
          this.currentCalculationStatus = this.rawMatOrSampleService.getCurrentCalculationStatus();
          this.reset(true);
          this.updateForcedCalcStatusForm();
          this.toaster.toast(this.translate.instant('msg.user.rawmaterial.result.save.valid'));
          this.valueFields.forEach((item, _idx, _items) => {
            item.nutrimentValue.control.markAsPristine();
          });
          this.referenceAreChanged = this.rawMatOrSampleService.getReferenceAreChanged();
          // Implicitly do not show the confirmation popup to leave the page
          this.hasBeenTouched = false;
        });
    };

    if (this.checkError()?.type === this.ERROR_INVALID) {
      this.showConfirmation(this.translate.instant('msg.user.rawmaterial.values.save.invalid'), saveCallback);
    } else if (this.rawMatOrSampleService.getIsCoherent()) {
      saveCallback();
    } else {
      this.showConfirmation(this.translate.instant(`msg.user.rawmaterial.values.save.coherence`), saveCallback);
    }
  }

  returnAnalysisList(rowData: RawMaterialNutrientDto): AnalysisOriginDto[] {
    if (this.loadingAnalysis) {
      return null;
    }
    const selectionType = this.getNutrientDataTable(rowData).returnTableSelection();
    switch (selectionType) {
      case ProductTableValueSelection.PROPOSED:
        return this.calcAnalysisArray;
      case ProductTableValueSelection.STD:
        return this.stdAnalysisArray;
      default :
        return this.allAnalysisesWithExclusion;
    }
  }

  returnSelectedAnalysis(rowData: RawMaterialNutrientDto): any {
    return this.getNutrientDataTable(rowData).analysisOrigin || null;
  }

  disableAnalysisList(rowData: RawMaterialNutrientDto): boolean {
    const productTableDate = this.getNutrientDataTable(rowData);
    return this.isReadOnly || productTableDate.selected || FunctionUtils.isNotDefinedOrEmptyString(productTableDate.value);
  }

  showAnalysisList(rowData: RawMaterialNutrientDto): boolean {
    const productTableDate = this.getNutrientDataTable(rowData);
    return productTableDate.selected || !FunctionUtils.isNotDefinedOrEmptyString(productTableDate.value);
  }

  onAnalysisChange(anaylysis: AnalysisOriginDto, rowData: RawMaterialNutrientDto): void {
    const productTable = this.getNutrientDataTable(rowData);
    if (!anaylysis) {
      productTable.analysisOrigin = null;
      return;
    }
    productTable.analysisOrigin = anaylysis;
  }

  onAnalysisClear(rowData: RawMaterialNutrientDto): void {
    this.onAnalysisChange(null, rowData);
  }

  /**
   * Return the right tooltip text
   *
   * @param rowData line of the table
   */
  getRightTooltipText(rowData: RawMaterialNutrientDto): string {
    return rowData.description === '' ? rowData.label : rowData.description;
  }

  /**
   * load the list of data for the current nutriment
   */
  async refreshDataPrimeNg() {
    const data = new PrimeTableDataModel();
    const fetchResult = await this.fetchData(this.referenceAreChanged);
    if (fetchResult) {
      data.results = this.getSortedRawMatNutListAccordingFacultative(fetchResult);
      this.mergeResults(data.results);
      this.computeCoherenceValue();
      await this.fetchProposedResults();
      data.paginate = false;
    } else {
      console.error('No fetched result ', fetchResult);
    }
    this.currentNutriment = data;
  }

  /**
   * Will move the non facultative items at the the end of the list
   * @param listToSort the list to sort
   * @returns the sorted list
   */
  getSortedRawMatNutListAccordingFacultative(listToSort: (RawMaterialNutrientDto | SampleNutrientDto)[]): (RawMaterialNutrientDto | SampleNutrientDto)[] {
    const nonFacultativeRawMat: RawMaterialNutrientDto[] = [];
    const facultativeRawMat: RawMaterialNutrientDto[] = [];
    listToSort.forEach((rawMatNut: RawMaterialNutrientDto | SampleNutrientDto) => {
      if (rawMatNut.facultativeInput) {
        facultativeRawMat.push(rawMatNut);
      } else {
        nonFacultativeRawMat.push(rawMatNut);
      }
    });
    return [...nonFacultativeRawMat, ...facultativeRawMat];
  }

  /**
   * Toggle all checkbox which are shown
   * @param event the value of the trigged the event
   */
  toggleAll(event: any) {
    this.getNutrients().forEach(rawMat => {
      rawMat.selected = rawMat.canApplyStandardOrProposedValue() && event.checked;
      this.updateSelectedAnalysis(rawMat);
      this.saveCheckboxStatus(rawMat.code, rawMat.selected);
    });
  }

  getRegisteredClass(entity: RawMaterialNutrientDto): string {
    return 'sample-mode ' + (entity.filled ? 'filled' : 'modified');
  }

  formatNumber(value: number, decimal: number): any {
    if (FunctionUtils.isDefined(value) && FunctionUtils.isDefined(decimal)) {
      return value.toFixed(decimal);
    } else {
      return value;
    }
  }

  /**
   * Get the tool tip message for the value input msg
   * @param row the row where the input is
   * @returns the corresponding tooltip message
   */
  getValueTooltipMsg(row: RawMaterialNutrientDto): string {
    const floorCeilResult = this.getFloorCeilResult(row);
    if (!floorCeilResult.limitReached) {
      return '';
    }
    const firstLine = this.translate.instant('msg.user.rawmaterial.entry-nutrient-ceil-floor-reached');
    const ceilValueLine = this.translate.instant('label.common.ceiling-value') + " : " + (this.formatNumber(floorCeilResult.ceilVal, row.decimal) || '-');
    const floorValueLine = this.translate.instant('label.common.floor-value') + " : " + (this.formatNumber(floorCeilResult.floorVal, row.decimal) || '-');
    return firstLine
      + '\n   + ' + ceilValueLine
      + '\n   + ' + floorValueLine;
  }

  /**
   * Evaluate the class of the input connected to the row representing by rawMatNutDto
   * @param rawMatNutDto the corresponding row
   * @returns the class string to put in the input
   */
  getValueClass(rawMatNutDto: RawMaterialNutrientDto): string {
    const rawMatNutDataTable: RawMaterialOrSampleNutrientValueTableData = this.getNutrientDataTable(rawMatNutDto);
    if (!rawMatNutDataTable || !rawMatNutDto) {
      return 'sample-mode';
    }
    const tolerant = this.isTolerant(rawMatNutDto, rawMatNutDataTable) ? '' : 'tolerance';
    const isFloorCeilReached: boolean = this.getFloorCeilResult(rawMatNutDto).limitReached;
    const floorCeilClass = isFloorCeilReached ? 'floor-ceil-reached' : '';
    const filled = (!isFloorCeilReached &&  rawMatNutDataTable.filled) ? 'filled' : 'modified';
    const coherence = !rawMatNutDataTable.filled && FunctionUtils.isDefined(rawMatNutDataTable.proposed) && rawMatNutDataTable.proposed !== rawMatNutDataTable.value ? 'coherence' : '';
    return `${floorCeilClass} ${filled} ${tolerant} ${coherence} sample-mode`;
  }

  calculateDiff(entity: RawMaterialNutrientDto, value: any): string {
    let diff = null;
    if (FunctionUtils.isDefined(entity.value) && FunctionUtils.isDefined(value)) {
      const d = Number.parseFloat(value) - entity.value;
      const rounded = Math.round(d * (Math.pow(10, 10))) / Math.pow(10, 10);
      if (rounded > 0) {
        diff = `+${rounded}`;
      } else if (rounded < 0) {
        diff = `${rounded}`;
      }
    }
    return diff;
  }

  /**
   * Check if the calc status can be forced regardless to right
   * @returns true if the calc status can be forced
   */
  enableForceCalcStatus(): boolean {
    const isBlockedBefore = this.isCurrentCalcStatusBlockedAfterCalc();
    const isValid = this.isCurrentCalcStatusValid();
    const isEmpty = this.isCurrentCalcStatusEmpty();
    return !isEmpty && (isBlockedBefore || (isValid && this.isCurrentCalcStatusForced()));
  }

  /**
   * Check if the current calculation status is a forced one
   * @returns true if the current calc status is a forced one
   */
  isCurrentCalcStatusForced(): boolean {
    return (this.calculationId && this.rawMatOrSampleService.getTempForcedCalcStatus())
      || (!this.calculationId && this.rawMaterialOrSample?.forcedCalcStatus);
  }

  getDiffClass(value: string): string {
    let clazz = '';
    if (value) {
      if (value.startsWith('+')) {
        clazz = 'positive';
      } else {
        clazz = 'negative';
      }
    }
    return clazz;
  }

  checkError(): { type: string, message: string} {
    if (!this.hasRights) {
      return {
        type: this.ERROR_READ_ONLY,
        message: this.translate.instant('msg.common.no-rights')
      };
    }
    if(this.rawMaterialOrSample?.status===RawmaterialOrSampleStatus.DISABLED) {
      return {
        type: this.ERROR_READ_ONLY,
        message: this.translate.instant(this.traductionKeys.disabledBy,
          { param: this.rawMaterialOrSample.disabledBy })
      };
    }
    if (this.rawMaterialOrSample?.status===RawmaterialOrSampleStatus.ARCHIVED) {
      return {
        type: this.ERROR_READ_ONLY,
        message: this.translate.instant(this.traductionKeys.archivedBy,
          { param: this.rawMaterialOrSample.archivedBy })
      };
    }
    if (this.isReadOnly && this.rawMaterialOrSample) {
      return {
        type: this.ERROR_READ_ONLY,
        message: this.translate.instant(this.traductionKeys.lockedBy,
          {param: this.rawMaterialOrSample.lockUserFullName})
      };
    }
    if (!this.isReadOnly && this.valueFields) {
      const errors = this.valueFields.filter((item, _i, _fields) => item.nutrimentValue.invalid);
      if (errors.length !== 0) {
        return {
          type: this.ERROR_INVALID,
          message: this.translate.instant(`msg.user.rawmaterial.values.invalid`)
        };
      }
      const classes = this.valueFields.map((item, _i, _fields) => this.getValueClass(item.entity));
      if (classes.some(classe => classe.includes('tolerance'))) {
        return {
          type: this.ERROR_TOLERANCE,
          message: this.translate.instant('msg.user.rawmaterial.values.tolerance')
        };
      }
      if (classes.some(classe => classe.includes('coherence'))) {
        return {
          type: this.ERROR_COHERENCE,
          message: this.translate.instant('msg.user.rawmaterial.values.coherence')
        };
      }
    }
    return null;
  }

  focusField(model: any, next: boolean): void {
    const fields = this.valueFields.toArray();
    const input = model.valueAccessor._elementRef.nativeElement;
    let focus: any;
    this.valueFields.forEach((item, index, _fields) => {
      if (input === this.getNativeElement(item)) {
        if (next && index !== fields.length - 1) {
          focus = this.getNativeElement(fields[index + 1]);
        } else if (!next && index !== 0) {
          focus = this.getNativeElement(fields[index - 1]);
        }
      }
    });
    if (focus) {
      focus.focus();
    }
  }

  isCalculable(): boolean {
    const nutrients = this.valueFields.toArray();
    return nutrients.length !== 0 && nutrients.every(item => {
      const control: AbstractControl = item.nutrimentValue.control;
      const rawMatNutDataTable = this.getNutrientDataTable(item.entity);
      const isFacultative: boolean = rawMatNutDataTable?.facultative;
      return (isFacultative && !control.errors?.pattern) ||
        (!FunctionUtils.isNotDefinedOrEmptyString(control.value) && (!control.errors?.pattern));
    });
  }

  canExport(): boolean {
    return this.exportService.canExport(this.rawMaterialOrSample);
  }

  exportEnabled(): boolean {
    return this.exportService.exportEnabled();
  }

  returnExportTooltip(): string {
    return this.exportService.returnExportTooltip();
  }

  exportRequest() {
    if (!this.canExport()) {
      return;
    }

    const confirmationMessage = this.translate.instant('label.user.export.confirmation.multiple.rm');

    const dialogRef = this.dialogMat.open(RawmaterialsExportDialogComponent, {
      data: {
        message: confirmationMessage,
        rawMaterials: this.rawMaterialOrSample,
        defaultFormat: this.userService.getEntityDto().rawmatExportFormat,
        listFormat: this.userService.getEntityDto().rawmatExportList
      }
    });

    dialogRef.afterClosed().subscribe((optionnalFormatChose) => {
      if (optionnalFormatChose) {
        const request: ExportMultiFormatRequestDto = {
          refreshToken: 'DEPRECATED',
          rawMaterialsCodes: [{
            entityCode: this.rawMaterialOrSample.entity.identifier,
            rmCodes: [{rmCode: this.rawMaterialOrSample.code}]
          }],
          exportFormatList: optionnalFormatChose
        };
        this.entityService.rawMaterialsexportnotificationExportMultiFormatNotification(this.userService.getEntity(), request)
          .subscribe(() => {
            this.toaster.toast(this.translate.instant('label.user.export.request-successful'));
          });
      }
    });
  }

  isApplyable(): boolean {
    return this.getNutrients().some(item => item.selected);
  }

  valueFilled(rawMatNut: RawMaterialNutrientDto, field: any): void {
    const tableData = this.getNutrientDataTable(rawMatNut);
    if (field.dirty) {
      tableData.filled = true;
      /* Automatically select if the value is not defined AND the standard or proposed value is applyable */
      tableData.selected = tableData.canApplyStandardOrProposedValue() && FunctionUtils.isNotDefinedOrEmptyString(tableData.value);
      this.calculationId = null;
      this.saveCheckboxStatus(rawMatNut.nutrientCode, tableData.selected);
    }
    this.hasBeenTouched = true;
    this.updateSelectedAnalysis(tableData);
    this.fetchProposedResults();
    this.computeCoherenceValue();
  }

  checkboxEvent(rowData: RawMaterialNutrientDto) {
    this.calculationId = null;
    const tableData: RawMaterialOrSampleNutrientValueTableData = this.getNutrientDataTable(rowData);
    this.updateSelectedAnalysis(tableData);
    const selectionStatus = this.getNutrientDataTable(rowData)?.selected;
    if(rowData.nutrientCode) {
      this.saveCheckboxStatus(rowData.nutrientCode, selectionStatus);
    }
  }

  /**
   * Update the selected analysis of the table data
   *
   * @param tableData the table data where the analysis must be potentially updated
   */
  updateSelectedAnalysis(tableData: RawMaterialOrSampleNutrientValueTableData): void {
    const selectionType: ProductTableValueSelection = tableData.returnTableSelection();
    switch (selectionType) {
      case ProductTableValueSelection.STD:
        tableData.analysisOrigin = this.stdAnalysis;
        break;
      case ProductTableValueSelection.PROPOSED:
        tableData.analysisOrigin = this.calcAnalysis;
        break;
      case ProductTableValueSelection.OTHER:
        if (
          FunctionUtils.isNotDefinedOrEmptyString(tableData.value)
          || !tableData.analysisOrigin
          || this.isStandarOrCalcAnalysis(tableData.analysisOrigin)
        ) {
          // For the analysis of sample only if necessary
          tableData.analysisOrigin = this.analysisFromSample;
        }
        break;
      default:
        console.error("Case not handled in updateSelectedAnalysis for this type of selection ", selectionType);
        break;
    }
  }

  /**
   * Update all selected analysis of table data
   */
  updateAllSelectedAnalysis(): void {
    Object.values(this.nutrients).forEach(nut => this.updateSelectedAnalysis(nut));
  }

  /**
   * Check if the analysis origin is standard of calculated one
   *
   * @param analysis the analysis to test
   * @returns true if the analysis is std or calculated
   */
  isStandarOrCalcAnalysis(analysis: AnalysisOriginDto): boolean {
    return analysis?.code && STD_AND_CALC_ANALYSIS_CODE.includes(analysis.code);
  }

  /**
   * Reset the table
   * @param keepFormerStates at true if you want to keep the former state
   * @param withRefresh refresh the data table or not.
   */
  reset(keepFormerStates: boolean, withRefresh: boolean = true): void {
    this.calculationId = null;
    this.rawMatOrSampleService.clearValues();
    if (keepFormerStates) {
      const currentNutValues = this.getNutrients();
      currentNutValues.forEach((rawMatTable) => {
        /* To reset the values when we will merge the result */
        rawMatTable.eraseAttributes = true;
      });
      /* Set the store to retrieve the state when merge the results after calling refreshDataPrimeNg */
      this.rawMatOrSampleService.setValues(currentNutValues);
    } else {
      this.nutrients = {};
    }
    if (withRefresh) {
      this.refreshDataPrimeNg();
    }
  }

  calculate(): void {
    this.rawMatOrSampleService.setProductId(this.productId);
    const calculation = this.getCalculationParameters();
    this.rawMatOrSampleService.saveValues(this.getNutrients(), this.notes.value, this.coherenceResult);

    this.rawMatOrSampleService.setReference(this.reference);
    this.rawMatOrSampleService.setPredictionModel(this.predictionModel);
    this.rawMatOrSampleService.setReferenceAreChanged(this.referenceAreChanged);

    this.loading = true;
    let isCoherent: boolean = this.gauge?.isIncoherent();
    isCoherent = FunctionUtils.isDefined(isCoherent) ? isCoherent : true;
    this.equationManagerService.calculate(calculation).pipe(catchError((err) => {
      this.loading = false;
      let error: NeqErrorModel;
      try {
        error = err.error;
      } catch (e) {
        this.dialogService.showErrorDetail(err, this.translate.instant(`error.user.rawmaterial.values.calculus`));
        console.error('unknown error : ');
        console.dir(e);
        return EMPTY;
      }
      let detail = error.message;
      if (FunctionUtils.isDefined(error.detail)) {
        detail = detail + ',' + error.detail;
      }
      this.dialogService.showErrorSimple(this.translate.instant('error.user.rawmaterial.values.calculus'), detail);
      return EMPTY;
    })).subscribe((computeWithMetaDatas) => {
      if (computeWithMetaDatas.status === 'FINISHED') {
        this.loading = false;
        this.rawMatOrSampleService.setIsCoherent(isCoherent);
        const calcStatusFromComput: CalculationStatus = computeWithMetaDatas.calculationStatus;
        this.calculationId = computeWithMetaDatas.id;
        this.computeWithMeta = computeWithMetaDatas;
        this.rawMatOrSampleService.setComputeWithMeta(computeWithMetaDatas);
        this.rawMatOrSampleService.setError(this.checkError());
        this.rawMatOrSampleService.setTempCalculationStatus(calcStatusFromComput);
        this.rawMatOrSampleService.setTempForcedCalcStatus(computeWithMetaDatas.forcedCalcStatus);
        /* Update the current because the calc status of ComputeDto might change the current one */
        this.currentCalculationStatus = this.rawMatOrSampleService.getCurrentCalculationStatus();
        this.rawMatOrSampleService.setCalculationId(computeWithMetaDatas.id);
        /* Reset the previous blocked results */
        this.resetCurrentBlockedResultsTreatment.next(null);
        this.allBlockedResults.next(null);
        this.computeChange.next(computeWithMetaDatas);
        this.updateForcedCalcStatusForm();
        if (!computeWithMetaDatas.facultativeNutError) {
          /* Will trig function in the view rawmaterials-manage.page.ts via subscription of this.route.queryParams in the same view */
          this.router.navigate([`/app/${this.pathComplement}/input/${this.productId}`], {queryParams: {result: computeWithMetaDatas.id}});
        }
      } else {
        this.loading = false;
        const neqError = this.convertComputeErrorToNeqError(computeWithMetaDatas);
        this.dialogService.showErrorDetail(neqError, this.translate.instant(`error.user.rawmaterial.values.calculus`));
      }
    });
  }

  /**
   * Check if the current calculation status if valid
   * @returns true if the current calculation is valid
   */
  isCurrentCalcStatusValid(): boolean {
    return CalculationStatusService.isCalcStatusValid(this.currentCalculationStatus);
  }

  /**
   * Check if the current calculation status if blocked after calc
   * @returns true if the current calculation is blocked after calc
   */
  isCurrentCalcStatusBlockedAfterCalc(): boolean {
    return CalculationStatusService.isCalcStatusBlockedAfterCalc(this.currentCalculationStatus);
  }

  /**
   * Check if the current calculation status if blocked after calc
   * @returns true if the current calculation is blocked after calc
   */
  isCurrentCalcStatusBlockedBeforeCalc(): boolean {
    return CalculationStatusService.isCalcStatusBlockedBeforeCalc(this.currentCalculationStatus);
  }

  /**
   * Check if the current calculation status if blocked after calc
   * @returns true if the current calculation is blocked after calc
   */
  isCurrentCalcStatusEmpty(): boolean {
    return CalculationStatusService.isCalcStatusEmpty(this.currentCalculationStatus);
  }

  /**
   * Request all blocked results
   */
  private requestBlockedResults(): void {
    this.resetCurrentBlockedResultsTreatment.next(null);
    this.allBlockedResults.next(null);
    this.loadingBlockedResults = true;
    this.rawMatOrSampleService.retrieveBlockedResult(this.rawMaterialOrSample.id, this.calculationId)
      .pipe(takeUntil(this.resetCurrentBlockedResultsTreatment))
      .subscribe((res) => {
        this.allBlockedResults.next(res);
        this.loadingBlockedResults = false;
      });
  }

  /**
   * Show the popin of blocked results when the conditions matched
   */
  showPopinOfBlockedResultsWhenReady(): void {
    if (!this.canShowPopinOfBlockedResult()) {
      return;
    }
    if (!this.loadingBlockedResults && FunctionUtils.isNotDefined(this.allBlockedResults.value)) {
      this.requestBlockedResults();
    }
    this.waitingForBlockedResultsForPopin = true;
    if (FunctionUtils.isDefined(this.allBlockedResults.value)) {
      this.showPopinOfBlockedResults(this.allBlockedResults.value);
      return;
    }
    const subsciption = this.allBlockedResults
      .pipe(takeUntil(this.resetCurrentBlockedResultsTreatment))
      .subscribe((allBlockedResult) => {
        if (FunctionUtils.isDefined(allBlockedResult)) {
          this.showPopinOfBlockedResults(allBlockedResult);
          subsciption.unsubscribe();
        }
      });
  }

  /**
   * Show the popin for blocked results without any check of condition
   * @param blockedResult the blocked results to show
   */
  private showPopinOfBlockedResults(blockedResult: ResultColumnDto[]): void {
    this.waitingForBlockedResultsForPopin = false;
    const inputPoppedInData: ResultColumnDto[] = blockedResult;

    this.dialogMat.open(NeqRawMaterialBlockedResultsPopinComponent, {
      data: {
        args: inputPoppedInData
      },
      maxHeight: '70vh'
    });
  }

  /**
   * Check if the popin of blocked results can be shown
   * @returns true if it can be shown
   */
  canShowPopinOfBlockedResult(): boolean {
    return this.isCurrentCalcStatusBlockedAfterCalc() && (!this.computeWithMeta?.facultativeNutError);
  }

  private convertComputeErrorToNeqError(compute: ComputeDto): NeqErrorModel {
    const neqError = new NeqErrorModel();

    neqError.type = 'compute';
    neqError.code = `status=${compute.status} by ${compute.createdBy}}`;
    neqError.message = compute.report;
    neqError.uuid = `computeId=${compute.id}`;
    neqError.time = Date.now() / 1000;
    return neqError;
  }

  apply(): void {
    this.getNutrients().forEach(nutrient => {
      if (nutrient.selected) {
        nutrient.value = nutrient.getNeededValueWhenSelected();
        nutrient.filled = false;
      }
    });
    this.hasBeenTouched = true;
    // set the selected fields pristine
    this.valueFields.forEach((item, _i, _fields) => {
      const nutrient = this.nutrients[item.entity.nutrientCode];
      if (nutrient.selected) {
        item.nutrimentValue.control.markAsPristine();
      }
    });
    this.fetchProposedResults();
    this.computeCoherenceValue();
  }

  private async initData() {
    // In semiauto context we use the RM entity insteadof current logged entity
    this.importSemiAutoIsOnGoing = this.importSemiAutoStore.isProcessIsOnGoing();
    /* even if it is not the entity of the raw mat we need to send the entity
     * of the one selected by the user before coming here */
    this.rawMatOrSampleService.retrieveProductById(this.productId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(rawMatOrSample => {
        this.rawMaterialOrSample = rawMatOrSample;
        if (!this.userService.isFeedOperatingMode()) {
          // Test the the edition authorization is tested on some sample attributes
          this.hasRights = this.sampleService.canEdit(this.rawMaterialOrSample as SampleDto);
        }
        this.analysisFromSample = (rawMatOrSample as SampleDto).analysisOrigin || null;
        let forceToFetchBlockedResult: boolean = false;
        if (this.productId !== this.rawMatOrSampleService.getProductId()) {
          this.rawMatOrSampleService.setProductId(this.productId);
          forceToFetchBlockedResult = true;
        }
        if (this.rawMatOrSampleService.getValues().length > 0) {
          if (this.rawMaterialOrSample.notes === this.rawMatOrSampleService.getNotes()) {
            this.notes.patchValue(this.rawMaterialOrSample.notes);
          } else {
            // mark as dirty if note not same as in raw-material, to enable save button
            this.notes.patchValue(this.rawMatOrSampleService.getNotes());
            this.notes.markAsDirty();
          }
        } else {
          this.notes.patchValue(this.rawMaterialOrSample.notes);
        }
        this.rawMatOrSampleService.setProductCalculationStatus(rawMatOrSample.calculationStatus);
        /* Update the current because the calc status of RawMaterialDto might change the current one */
        this.currentCalculationStatus = this.rawMatOrSampleService.getCurrentCalculationStatus();
        if (this.rawMaterialOrSample.entity) {
          this.subEntityService.getByCode(this.rawMaterialOrSample.entity.identifier)
            .subscribe(entity => {
              this.entity = entity;
              this.defineEntityLogo();
            });
        }
        const blockResults = this.isCurrentCalcStatusBlockedAfterCalc() && (FunctionUtils.isNotDefined(this.allBlockedResults.value) || forceToFetchBlockedResult);
        if (blockResults) {
          this.requestBlockedResults();
        }
        this.headerService.setHeaderValues({
          strongLabel: this.rawMaterialOrSample.code,
          basicLabel: ' - ' + this.rawMaterialOrSample.label,
          showGoBack: true,
          redirectionRoute: this.activatedRoute.parent.parent.snapshot
        });
        this.updateForcedCalcStatusForm();
        if(this.rawMaterialOrSample.status == RawmaterialOrSampleStatus.ENABLED){
          this.rawMatOrSampleService.patch(this.productId, {patchAction: 'LOCK_ENTITY'})
          .subscribe(
            {
              next: () => {
                const isReadOnly = this.rawMaterialOrSample.status == RawmaterialOrSampleStatus.DISABLED || this.rawMaterialOrSample.status == RawmaterialOrSampleStatus.ARCHIVED;
                this.updateIsReadOnly(isReadOnly, true);
              },
              error: () => {
                this.updateIsReadOnly(true, true);
              }
            }
          );
        }
        this.fetchReferenceMetadata().then(_ => {
          this.loading = false;
          this.refreshDataPrimeNg();
        });
      });
  }

  /**
   * Define entity Logo
   */
  defineEntityLogo() {
    if (this.entity.logo) {
      this.entityLogo = this.domSanitizer.bypassSecurityTrustUrl(this.entity.logo);
    } else {
      this.entityLogo = undefined;
    }
  }

  getNutrientTechnicalDocUrl(nutrient: RawMaterialNutrientDto) {
    if (nutrient?.technicalDocAvailable) {
      this.technicalDocService.getNutrientTechnicalDoc(`${nutrient.nutrientCode}.${nutrient.technicalDocFormat}`);
    }
  }

  getPredictionModelTechnicalDocUrl() {
    if (this.predictionModel?.technicalDocAvailable) {
      this.technicalDocService.getPredictionModelTechnicalDoc(`${this.predictionModel.code}.${this.predictionModel.technicalDocFormat}`);
    }
  }

  /**
   * Check if the value entered in row has reached the ceil and floor limit
   * @param row the row where you want to check if floor and ceil limits have been reached
   * @returns the object wich correspond to the floor ceil result
   */
  getFloorCeilResult(row: RawMaterialNutrientDto): FloorCeilResult {
    const floorCeilResult = new FloorCeilResult();
    const rawMatNutDataTable = this.getNutrientDataTable(row);
    if (!rawMatNutDataTable) {
      floorCeilResult.limitReached = false;
      return floorCeilResult;
    }
    const rawValue: any = rawMatNutDataTable.value;
    const isCappable = row.cappableNutrient && row.cappableRefVal;
    if (!isCappable || FunctionUtils.isNotDefined(rawValue) || (!rawValue && rawValue === '')) {
      floorCeilResult.limitReached = false;
      return floorCeilResult;
    }
    const minVal = row.min;
    const maxVal = row.max;
    const realFloorVal = FunctionUtils.isDefined(row.floorVal) ?
      row.floorVal
      : FloorCeilCalculationService.getDefaultFloorVal(minVal, maxVal);
    const realCeilVal = FunctionUtils.isDefined(row.ceilVal) ?
      row.ceilVal
      : FloorCeilCalculationService.getDefaultCeilVal(minVal, maxVal);
    const valueNumber: number = +rawValue;
    const isFloorLimit: boolean = valueNumber < realFloorVal;
    const isCeilLimit: boolean = valueNumber > realCeilVal;
    floorCeilResult.limitReached = isFloorLimit || isCeilLimit;
    floorCeilResult.ceilVal = realCeilVal;
    floorCeilResult.floorVal = realFloorVal;
    return floorCeilResult;
  }

  private getNativeElement(item: NutrimentValueDirective) {
    return item.nutrimentValue.valueAccessor._elementRef.nativeElement;
  }

  private getNutrients(): RawMaterialOrSampleNutrientValueTableData[] {
    return Object.values(this.nutrients);
  }

  private fetchData(withref: boolean = false): Promise<(RawMaterialNutrientDto | SampleNutrientDto)[]> {
    return lastValueFrom(
      this.rawMatOrSampleService
        .retrieveProductNutrientsByEntityCode(
          this.userService.getEntity(),
          this.productId,
          withref ? this.reference : null
        )
    );
  }

  private mergeResults(results: (RawMaterialNutrientDto | SampleNutrientDto)[]) {
    const cache = this.rawMatOrSampleService.getValues();
    this.firstMergeOfResults = !cache || cache.length == 0;
    for(const rawMatNutDto of results) {
      let rawMatTableData = cache.find((x) => x.code === rawMatNutDto.nutrientCode);
      const tableDataDidNotExist = !rawMatTableData;
      if (tableDataDidNotExist) {
        // create a new one
        rawMatTableData = new RawMaterialOrSampleNutrientValueTableData(rawMatNutDto);
        if (!rawMatTableData.analysisOrigin) {
          rawMatTableData.analysisOrigin = this.analysisFromSample;
        }
        // To store all attributes
        rawMatTableData.eraseAttributes = true;
      }
      // add it to the list
      this.nutrients[rawMatNutDto.nutrientCode] = rawMatTableData;
      rawMatTableData.required = rawMatNutDto.required;
      rawMatTableData.facultative = rawMatNutDto.facultativeInput;
      rawMatTableData.standardFromRawMatNutDto = rawMatNutDto.standard;
      if (rawMatTableData.eraseAttributes) {
        if (rawMatNutDto.filled) {
          rawMatTableData.filled = true;
          rawMatTableData.value = rawMatNutDto.value;
        } else {
          rawMatTableData.filled = false;
          rawMatTableData.value = null;
        }
      }

      // No check with Current import RM and current displaying RM are available
      if (tableDataDidNotExist && this.importSemiAutoIsOnGoing
        && this.importSemiAutoStore.getCurrentRmValue().rawMaterial.id == this.productId) {
        const n = this.importSemiAutoStore.getCurrentRmValue().inputs.find(n => n.code == rawMatNutDto.nutrientCode);
        if (n) {
          rawMatTableData.filled = true;
          rawMatTableData.value = n.value;
        }
      }
      /* For the next merge we will not erase the saved attributes */
      rawMatTableData.eraseAttributes = false;
      if (tableDataDidNotExist) {
        rawMatTableData.selected = this.getCheckboxStatus(rawMatTableData.code);
      }
    }
  }

  /**
   * Checkboxes selections made by the user
   * @param checkboxCode code related to the inidividual checkbox in question
   * @return selection status
   */
  private getCheckboxStatus(checkboxCode: string): boolean {
    const statusId = this.productId + '-checkbox-' + checkboxCode;
    const value = localStorage.getItem(statusId);
    if(value) {
      return value == "1";
    } else {
      return false;
    }
  }

  /**
   * In order to keep the checkboxes statuses
   * @param checkboxCode code related to the inidividual checkbox in question
   * @param status selection status
   */
  private saveCheckboxStatus(checkboxCode: string, status: boolean): void {
    const statusId = this.productId + '-checkbox-' + checkboxCode;
    localStorage.setItem(statusId, status?"1":"0");
  }

  /**
   * On toggle event to force the calc status
   */
  onForceCalcStatus() {
    this.loadingForceStatus = true;
    this.rawMatOrSampleService.forceCalcStatus(this.calculationId)
      .pipe(finalize(() => this.loadingForceStatus = false))
      .subscribe({
        next: compute => {
          this.rawMatOrSampleService.setTempCalculationStatus(compute.calculationStatus);
          this.rawMatOrSampleService.setTempForcedCalcStatus(compute.forcedCalcStatus);
          this.currentCalculationStatus = this.rawMatOrSampleService.getCurrentCalculationStatus();
          this.updateForcedCalcStatusForm();
        },
        error: (error) => {
          /* In case of error reset the form */
          this.forcedCalcStatusForm.setValue(this.rawMatOrSampleService.getTempForcedCalcStatus(), {emitEvent: false});
          /* Throw error to display the build-in http error */
          throw error ;
        }
      });
  }

  /**
   * Will update the unblock calc status form according meta datas
   */
  updateForcedCalcStatusForm(): void {
    this.forcedCalcStatusForm.setValue(this.isCurrentCalcStatusForced(), {emitEvent: false});
    if (this.enableForceCalcStatus() && this.calculationId) {
      this.forcedCalcStatusForm.enable({emitEvent: false});
    } else {
      this.forcedCalcStatusForm.disable({emitEvent: false});
    }
  }

  /**
   * Check if the calc status toggle to force has to be displayed
   * @returns true if it has to be displayed
   */
  showForceCalcStatusToggle(): boolean {
    return (this.enableForceCalcStatus() || this.isCurrentCalcStatusBlockedBeforeCalc())
      && this.userService.hasRights(this.forceRmStatusRight)
      && !CalculationStatusService.isCalcStatusEmpty(this.currentCalculationStatus);
  }

  /**
   * Return the label for the toggle to unblock de calcStatus
   * @returns the label to display beside the toggle
   */
  getToggleCalcStatusLabel(): string {
    const textToTranslate = this.forcedCalcStatusForm.value ? 'label.common.unlocked' : 'label.common.unlock';
    return this.translate.instant(textToTranslate);
  }

  private fetchProposedResults(): Promise<void> {
      return new Promise<void>((resolve, _reject) => {
        if (!this.isReadOnly && this.hasCorrectFormat() && this.getNutrients().length > 0) {
          const request = this.getCalculationParameters(true);
          this.equationManagerService.calculateEntry(request)
            .subscribe(
              {
                next: result => {
                  this.canShowCheckboxes = this.hasRights;
                  if (result?.status === 'FINISHED') {
                    this.cleanAllNutrientsProposedValues();
                    this.mergeTableDataWithCalcEntryResults(result);
                  }
                  resolve();
                },
                error: (error) => {
                  resolve();
                  // retrow the error to get the build in popin
                  throw error;
                }
              });
        } else {
          this.canShowCheckboxes = this.hasRights && !this.isReadOnly;
          resolve();
        }
      })
  }

  mergeTableDataWithCalcEntryResults(computeDto: ComputeDto): void {
    for(const computeResult of computeDto.results) {
      const rawMatTable = this.nutrients[computeResult.nutrientCode];
      if (rawMatTable) {
        rawMatTable.proposed = computeResult.value;
      }
    }
    if (this.firstMergeOfResults) {
      for(const rawMatTable of this.getNutrients()) {
        /* Here to automatically select standard and proposed value on initialization */
        rawMatTable.selected = this.getCheckboxStatus(rawMatTable.code) || (this.canShowCheckboxes && !rawMatTable.filled && rawMatTable.canApplyStandardOrProposedValue());
        this.updateSelectedAnalysis(rawMatTable);
      }
    }
    this.firstMergeOfResults = false;
  }

  /**
   * Will update isReadOnly attributes and update meta datas
   * @param newValue the new value of read only
   * @param emitChangeEvent a true if you want to cahnge event for isReadOnly
   */
  private updateIsReadOnly(newValue: boolean, emitChangeEvent: boolean): void {
    this.isReadOnly = newValue;
    if (newValue) {
      this.notes.disable();
    } else {
      this.notes.enable();
    }
    if (emitChangeEvent) {
      this.readOnlyChange.emit(newValue);
    }
  }

  /**
   * Check from row if the checkbox for the standard/proposed value has to be show or not
   * @param row the row value of the table from which we want to know if the check box has to be shown
   * @returns true if the check box has to be shown
   */
  showStandardOrProposedCheckBox(row: RawMaterialNutrientDto): boolean {
    const rawMatNut = this.getNutrientDataTable(row);
    return this.canShowCheckboxes && rawMatNut?.canApplyStandardOrProposedValue();
  }

  /**
   * Verify if the checkbox for the standard/proposed value will be for standard value
   * @param row the row value of the table from which we want to verify the checkbox
   * @returns true if the checkbox is for standard value
   */
  isRowCheckBoxForStdValue(row: RawMaterialNutrientDto): boolean {
    const rawMatNut = this.getNutrientDataTable(row);
    return rawMatNut.isSelectionForStandardValue();
  }

  /**
   * Get the color of the checkbox for the standard/proposed value
   * @param row the row value of the table from which we want to get the checkbox color
   * @returns the corresponding checkbox color
   */
  getCheckboxColor(row: RawMaterialNutrientDto): string {
    return this.isRowCheckBoxForStdValue(row) ? "std-checkbox-color" : "proposed-checkbox-color";
  }

  onResetInput(): void {
    if (this.referenceAreChanged) {
      // Reset all input
      this.nutrients = {};
      // Reset the selected reference with the one from the sample or raw material
      this.modifyReferenceAction(this.rawMatOrSampleService.getReferenceOfSampleOrRawMat(), false);
    } else {
      this.reset(false);
    }
    this.rawMatOrSampleService.setTempCalculationStatus(null);
    this.rawMatOrSampleService.setTempForcedCalcStatus(false);
    // remove url parameter (which is result parameter) to implicitly disable save button in the result tab
    this.router.navigate([]);
    this.currentCalculationStatus = this.rawMaterialOrSample ? this.rawMaterialOrSample.calculationStatus : null;
    this.updateForcedCalcStatusForm();
  }

  /**
   * Get the Table data which correspond to RawMaterialNutrientDto
   * @param rawMatNutDto the RawMaterialNutrientDto from which we want the corresponding RawMaterialNutrient
   * @returns the corresponding TableData
   */
  getNutrientDataTable(rawMatNutDto: RawMaterialNutrientDto): RawMaterialOrSampleNutrientValueTableData {
    return this.nutrients?.[rawMatNutDto.nutrientCode];
  }

  /**
   * Get the tooltip text to show for the checkbox for the standard/proposed value
   * @param row the row value of the table from which we want the tooltip text
   * @returns the corresponding tooltip text
   */
  getRowCheckboxTooltip(row: RawMaterialNutrientDto): string {
    return this.isRowCheckBoxForStdValue(row) ?
      this.translate.instant('msg.user.rawmaterial.row.checkbox.std-value')
      : this.translate.instant('msg.user.rawmaterial.row.checkbox.proposed-value');
  }

  private cleanAllNutrientsProposedValues() {
    for(const nutrient of this.getNutrients()) {
      nutrient.proposed = null;
    }
  }

  private async fetchReferenceMetadata() {
    // Retrieve reference from rawmat if reference are not changed
    if (!this.referenceAreChanged) {
      this.reference = await lastValueFrom(this.rawMatOrSampleService.retrieveReference(this.rawMaterialOrSample));
      this.rawMatOrSampleService.setReferenceOfSampleOrRawMat(this.reference);
    }
    await this.fetchMetadataCommon();
  }

  private async fetchMetadataCommon() {
    this.predictionModel = await lastValueFrom(this.predictionModelService.get(this.reference.predictionModelId, {translated: true}));

    if (this.reference.coherenceEquationId) {
      const calculationCoherenceValue = new CalcRequestDto();
      calculationCoherenceValue.activityCode = this.userService.getActivity();
      calculationCoherenceValue.referenceId = this.reference.id;
      calculationCoherenceValue.referenceCode = this.reference.code;
      calculationCoherenceValue.predictionModelId = this.predictionModel.id;
      calculationCoherenceValue.productId = this.productId;

      if (this.hasRights) {
        this.coherences = await lastValueFrom(
          this.coherenceService.prepare(calculationCoherenceValue)
            .pipe(map(result => result.otherFacets))
        );
      }
    }
  }

  private computeCoherenceValue(): void {
    if (!this.coherences) {
      return;
    }
    // we need all predictors for coherence equation
    const checkCoherences = this.coherences.every(coherence => {
      return coherence.endsWith(':min') || coherence.endsWith(':std') || coherence.endsWith(':max') || coherence.endsWith(':filled')
        || (!this.nutrients[coherence] || FunctionUtils.isDefined(this.nutrients[coherence].value));
    });
    if (
      !this.isReadOnly &&
      this.predictionModel &&
      this.hasCorrectFormat() &&
      this.coherences &&
      FunctionUtils.isDefined(this.reference.coherenceEquationId) &&
      (this.coherences.length === 0 || checkCoherences)
    ) {
      const calculationCoherenceValue = new CalcRequestDto();
      calculationCoherenceValue.activityCode = this.userService.getActivity();
      calculationCoherenceValue.referenceId = this.reference.id;
      calculationCoherenceValue.referenceCode = this.reference.code;
      calculationCoherenceValue.predictionModelId = this.predictionModel.id;
      calculationCoherenceValue.productId = this.productId;
      calculationCoherenceValue.inputs = this.returnAllCalculationInputDto(true);

      this.coherenceService.calculate(calculationCoherenceValue).subscribe(computeResult => {
        if (computeResult !== null && typeof computeResult !== 'undefined') { // only 2 digits
          this.coherenceResult = Math.round(computeResult * 100) / 100;
        }
      });
    } else {
      this.coherenceResult = null;
    }
  }

  private showConfirmation(message: string, callback: Function): void {

    const dialog = this.dialogMat.open(NeqRawmaterialsValuesDialogComponent, {
      data: {
        message: message
      }
    });
    dialog.afterClosed().subscribe(result => {
      if (result) {
        callback();
      }
    });
  }

  private getCalculationParameters(forValuesSuggestions = false): CalcRequestDto {
    const calculation = new CalcRequestDto();
    calculation.activityCode = this.userService.getActivity();
    calculation.predictionModelId = this.predictionModel.id;
    calculation.productId = this.productId;
    calculation.referenceId = this.reference.id;
    calculation.inputs = this.returnAllCalculationInputDto(false);
    calculation.forValuesSuggestions = forValuesSuggestions;
    calculation.referenceCode = this.reference.code;
    if(calculation.activityCode == ActivityUtil.FODDER_MODE) {
      calculation.entityPrincipalMatrixIdentifier = this.entity.principalMatrix?.identifier;
    }
    return calculation;
  }

  /**
   * Return all CalculationInputDto configured in the browser
   * @param forDefinedValueOnly to get the list for defined value only
   * @returns all CalculationInputDto setted in the browser
   */
  private returnAllCalculationInputDto(forDefinedValueOnly: boolean): CalcRequestInputDto[] {
    let rawMatNutTableData = this.getNutrients();
    if (forDefinedValueOnly) {
      rawMatNutTableData = rawMatNutTableData.filter(x => !FunctionUtils.isNotDefinedOrEmptyString(x.value));
    }
    return rawMatNutTableData.map((rawMatNut) => {
      const cri = new CalcRequestInputDto();
      cri.facet = {
        urn: URNUtil.createNutrientUrn(this.userService.getActivity(), rawMatNut.code),
        identifier: rawMatNut.code
      };
      cri.filled = rawMatNut.filled;
      /* If we are here that means that the values are regarded as valid for calculation
       * So if the value is not defined or an empty string, which can be the case when we calculate entry
       * OR when it is a facultative nutrient, then send null value */
      cri.value = FunctionUtils.isNotDefinedOrEmptyString(rawMatNut.value) ? null : rawMatNut.value;
      cri.analysisOrigin = rawMatNut.analysisOrigin;
      return cri;
    });
  }

  private isTolerant(rawMatNutDto: RawMaterialNutrientDto, rawMatNutDataTable: RawMaterialOrSampleNutrientValueTableData): boolean {
    if (FunctionUtils.isDefined(rawMatNutDataTable.proposed) && FunctionUtils.isDefined(rawMatNutDataTable.value)) {
      let variable: number;
      if (rawMatNutDataTable.proposed >= rawMatNutDto.maxTolerance) {
        variable = rawMatNutDto.maxTolerance;
      } else if (rawMatNutDataTable.proposed <= rawMatNutDto.minTolerance) {
        variable = rawMatNutDto.minTolerance;
      } else {
        variable = rawMatNutDataTable.proposed;
      }
      const tolerance = variable * rawMatNutDto.percentageTolerance / 100;
      return ((rawMatNutDataTable.proposed - tolerance) <= +rawMatNutDataTable.value) && ((rawMatNutDataTable.proposed + tolerance) >= +rawMatNutDataTable.value);
    }
    return true;
  }

  navigateWithState(url: string) {
    this.validationService.checkModifications(() => {
      // fromInput state is indicating that we arrive to the raw material properties from the input window
      this.router.navigate([url], { state: { fromInput: true } })
    })
  }

  private hasCorrectFormat() {
    return this.valueFields.toArray().every(item => {
      const control = item.nutrimentValue.control;
      return !control.errors?.pattern;
    });
  }

  /**
   * Return the import type label to display on the window
   */
  returnImportTypeLabel(): string {
    let importLabel: string;
    const importType = RawmaterialOrSampleService.returnImportType(this.rawMaterialOrSample)
    if (!importType) {
      return '';
    } else if (importType === ImportType.MANUAL) {
      importLabel = this.translate.instant("label.common.manual");
    } else {
      importLabel = this.translate.instant("label.common.automatic");
    }
    return ` (${importLabel})`

  }

  /**
   * Check if the action button has to be shown
   *
   * @returns true if the action button has to be shown
   */
  showActionButton(): boolean {
    return !this.loading && !this.isReadOnly && (!this.isSampleMode || this.hasRights);
  }

  isFeedMode(): boolean {
    return this.userService.isFeedOperatingMode();
  }

  startModifyReference(): void {
    this.dialogMat.open(SimpleReferencesSearchPopinComponent, {
      data: {reference: this.reference}
    }).afterClosed()
      .subscribe(data => {
        if (data) {
          this.modifyReferenceAction(data.reference, true);
        }
      });
  }

  private modifyReferenceAction(newRef: ReferenceDto, newRefHasChanged: boolean): void {
    // actual Nutrient Values keep to init new référence
    const originalNutValues = this.getNutrients();
    // To show confirmation pop-in in case we leave the window
    this.hasBeenTouched = true;
    // clean table
    this.currentNutriment.results = [];
    this.reset(false, false);

    this.referenceAreChanged = newRefHasChanged;

    this.reference = newRef;

    this.fetchMetadataCommon()
      .then(async _ => {
        this.loading = false;
        const data = new PrimeTableDataModel();
        const fetchResult = await this.fetchData(true);
        if (fetchResult) {
          data.results = this.getSortedRawMatNutListAccordingFacultative(fetchResult);
          this.mergeResults(data.results);
          this.putOldValuesOnNutrients(originalNutValues)
          this.computeCoherenceValue();
          await this.fetchProposedResults();
          data.paginate = false;
        } else {
          console.error('No fetched result ', fetchResult);
        }

        this.currentNutriment = data;
      });
  }

  // Set oldValues in displayed nutrients
  private putOldValuesOnNutrients(oldValues: RawMaterialOrSampleNutrientValueTableData[]): void {
    const newNutValues = this.getNutrients();
    for(const rmNutrient of newNutValues) {
      const filtered = oldValues.find(oldRm => {
        return oldRm.code == rmNutrient.code;
      });
      if (filtered !== null && filtered !== undefined) {
        rmNutrient.value = filtered.value;
        rmNutrient.filled = filtered.filled;
        rmNutrient.analysisOrigin = filtered.analysisOrigin;
      }
    }
  }

  saveNotes(): void {
    this.rawMatOrSampleService.patch(this.productId,
      {patchAction: 'SAVE_NOTES', patchValue: this.notes.value}).subscribe(() => {
      this.toaster.toast(this.translate.instant('label.user.rawmaterial.notes.updated'));
      // This has been saved, no need to trouble the user with the back button alert
      this.notes.markAsPristine();
    });
  }
}
